import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { GIFT_CARD_TYPE_OPTIONS } from "../../../../helpers/catalogs/gift-card-type-options";
import ListGiftCards from "../../../common/ListGiftCards";
import { GiftCard } from "../../../../types/gift-card";
import Button from "../../../Button";
import styled from "styled-components";
import { breakpoints } from "../../../../utils/styles";
import NoContentMessage from "../../../common/NoContentMessage";
import UserWalletCardDetail from "./UserWalletCardDetail";
import { fetchUserWalletCards, userWalletCardDetailOpen } from "../../../../actions/creators/user/walletCards";
import { getWalletCards } from "../../../../reducers/user/wallet-cards";
import MDSpinner from "react-md-spinner";
import { useBreakpoints } from "../../../../hooks/use-breakpoints";
import UserWalletListCarousel from "./UserWalletListCarousel";
import Submenu from "../../../common/Submenu";

const CtaBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: start;
    gap: 1rem;

    @media screen and (max-width: ${breakpoints.phone}px) {
        flex-direction: column;
    }
    
    a {
        width: 100%;
    }
`;

type Props = {
    walletCards: GiftCard[];
    fetchUserWalletCards: () => void;
    openUserWalletCardDetail: (giftCard: GiftCard) => void;
};

const UserWalletListPage: React.FC<Props> = ({ walletCards, fetchUserWalletCards, openUserWalletCardDetail }) => {
    const [selectedCategory, setSelectedCategory] = useState<string | number>("");

    useEffect(() => {
        fetchUserWalletCards();
    }, [fetchUserWalletCards]);

    const filteredGiftCards = useMemo(() => {
        if (!selectedCategory) return walletCards;

        return walletCards.filter((card) => card.type === selectedCategory);
    }, [walletCards, selectedCategory]);

    if (null === walletCards) {
        return <div className="p-5 text-center"><MDSpinner singleColor="var(--primary)" /></div>;
    }

    return (
        <div>
            <p>
                Mantén a la mano todas tus tarjetas Cinemex y da clic para ver su saldo
                a detalle.
            </p>
            <Submenu
                items={GIFT_CARD_TYPE_OPTIONS}
                selectedId={selectedCategory}
                onSelect={(id) => setSelectedCategory(id)}
            />
            {filteredGiftCards.length
                ? <CardsList cards={filteredGiftCards} onSelect={openUserWalletCardDetail} />
                : (
                    <NoContentMessage
                        icon="fa-light fa-credit-card"
                        title="Aún no tienes tarjetas en tu Wallet."
                        style={{ maxWidth: "37.5rem" }}
                    >
                        <p>Compra tu primera tarjeta o agrega una física para tenerla siempre a la mano.</p>
                    </NoContentMessage>
                )}
            <CtaBox>
                <Button primary to="/usuario/wallet/comprar">
                    Comprar Tarjeta
                </Button>
                <Button secondary to="/usuario/wallet/agregar">
                    Agregar Tarjeta Física
                </Button>
            </CtaBox>
            <UserWalletCardDetail />
        </div>
    );
};

const CardsList: React.FC<{ cards: GiftCard[]; onSelect: (giftCard: GiftCard) => void; }> = ({ cards, onSelect }) => {
    const { desktop: isDesktop } = useBreakpoints();

    if (isDesktop) {
        return <UserWalletListCarousel
            onClick={giftCard => onSelect(giftCard as GiftCard)}
            items={cards}
        />;
    }

    return (
        <ListGiftCards
            animated={true}
            giftCards={cards}
            onSelectGiftCard={giftCard => onSelect(giftCard as GiftCard)}
            className="my-5"
        />
    );
}

const mapStateToProps = (state: any) => ({
    walletCards: getWalletCards(state),
});

const mapDispatchToProps = (dispatch: any) => ({
    fetchUserWalletCards: () => dispatch(fetchUserWalletCards()),
    openUserWalletCardDetail: (giftCard: GiftCard) => dispatch(userWalletCardDetailOpen(giftCard)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserWalletListPage);
